import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import authService from '../services/auth.service';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = sessionStorage.getItem('agentToken');
    if (token) {
      try {
        const response = await authService.getProfile();
        if (response.success) {
          setUser(response.agent);
        }
      } catch (error) {
        console.error('驗證失敗:', error);
        // 清除所有 sessionStorage
        sessionStorage.clear();
      }
    }
    setLoading(false);
  };

  const login = async (username, password) => {
    try {
      const response = await authService.login(username, password);

      if (response.success) {
        sessionStorage.setItem('agentToken', response.token);
        setUser(response.agent);

        // 檢查是否有其他設備被踢出
        if (response.kickedDevices > 0) {
          message.info(`已登出 ${response.kickedDevices} 個其他設備`);
        }

        // 檢查是否需要修改密碼
        if (response.needChangePassword) {
          message.warning('首次登入，請先修改密碼');
          navigate('/profile');
        } else {
          message.success('登入成功');
          navigate('/dashboard');
        }
        
        return { success: true };
      } else {
        return { success: false, error: response.error };
      }
    } catch (error) {
      return { success: false, error: error.message || '登入失敗' };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('登出錯誤:', error);
    } finally {
      // 清除所有 sessionStorage（包括 token 和其他緩存）
      sessionStorage.clear();
      setUser(null);
      navigate('/login');
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      const response = await authService.changePassword(oldPassword, newPassword);
      if (response.success) {
        message.success('密碼修改成功，請重新登入');
        logout();
        return { success: true };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const isAdmin = () => {
    return user?.level === 0 && user?.role !== 'observer';
  };

  const canViewAdmin = () => {
    return user?.level === 0; // 包括觀察者，只要是 level 0 就能查看
  };

  const canCreateAgent = () => {
    return user?.role !== 'observer'; // 移除層級限制 - 支持無限層級下開
  };

  const value = {
    user,
    loading,
    login,
    logout,
    changePassword,
    isAdmin,
    canViewAdmin,
    canCreateAgent,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};